import type { PlatformRole } from "../../api/types";

export type OperatorSectionId =
  | "overview"
  | "customers"
  | "invitations"
  | "billing"
  | "support"
  | "reliability"
  | "notices"
  | "ownership";

export interface OperatorSection {
  id: OperatorSectionId;
  label: string;
  description: string;
  roles: readonly PlatformRole[];
}

const allOperatorRoles = [
  "platform_owner",
  "billing_operator",
  "support_operator",
  "security_operator",
  "auditor",
] as const satisfies readonly PlatformRole[];

export const operatorSections: readonly OperatorSection[] = [
  {
    id: "overview",
    label: "Overview",
    description:
      "Monitor customer growth, subscription health, delivery state, and platform activity.",
    roles: allOperatorRoles,
  },
  {
    id: "customers",
    label: "Customer accounts",
    description: "Review customer accounts, onboarding progress, and account access.",
    roles: allOperatorRoles,
  },
  {
    id: "invitations",
    label: "Invitations",
    description:
      "Create a customer workspace and bind its commercial offer to a secure owner invitation.",
    roles: ["platform_owner"],
  },
  {
    id: "billing",
    label: "Plans and billing",
    description:
      "Manage plans, entitlements, custom capacity requests, and subscription interventions.",
    roles: ["platform_owner", "billing_operator"],
  },
  {
    id: "support",
    label: "Customer support",
    description: "Use tenant-authorized snapshots and record internal operational notes.",
    roles: ["platform_owner", "support_operator", "security_operator"],
  },
  {
    id: "reliability",
    label: "Reliability",
    description: "Investigate dead-letter jobs and manage platform incidents.",
    roles: ["platform_owner", "security_operator", "auditor"],
  },
  {
    id: "notices",
    label: "Service notices",
    description: "Publish time-bound service communications to tenants and operators.",
    roles: allOperatorRoles,
  },
  {
    id: "ownership",
    label: "Ownership",
    description: "Verify or reject accepted tenant ownership transfers.",
    roles: allOperatorRoles,
  },
];

export function operatorSectionsForRole(role: PlatformRole): readonly OperatorSection[] {
  return operatorSections.filter((section) => section.roles.includes(role));
}

export function operatorSectionFromPath(pathname: string): OperatorSection | undefined {
  const [root, sectionId, ...remainder] = pathname.split("/").filter(Boolean);
  if (root !== "operator" || !sectionId || remainder.length > 0) return undefined;
  return operatorSections.find((section) => section.id === sectionId);
}

export function operatorSectionPath(section: OperatorSectionId): string {
  return `/operator/${section}`;
}

export function operatorRoleCanAccess(role: PlatformRole, section: OperatorSectionId): boolean {
  return operatorSections.some((item) => item.id === section && item.roles.includes(role));
}

export function operatorRoleCanMutate(role: PlatformRole, section: OperatorSectionId): boolean {
  switch (section) {
    case "customers":
    case "invitations":
    case "notices":
    case "ownership":
      return role === "platform_owner";
    case "billing":
      return role === "platform_owner" || role === "billing_operator";
    case "support":
      return (
        role === "platform_owner" || role === "support_operator" || role === "security_operator"
      );
    case "reliability":
      return role === "platform_owner" || role === "security_operator";
    case "overview":
      return false;
  }
}
